/* =========================================================================
   Ajinkya Big Bazaar — theme.js
   Vanilla JS only. No external libraries.
   Modules: overlay/drawers, mobile menu, header search, hero slider,
            scroll reveal, quantity inputs, product gallery + variants,
            AJAX cart (Shopify Cart API) with drawer rendering.
   ========================================================================= */
(function () {
  'use strict';

  const $ = (sel, scope) => (scope || document).querySelector(sel);
  const $$ = (sel, scope) => Array.from((scope || document).querySelectorAll(sel));
  const money = (cents) =>
    (window.Shopify && window.Shopify.formatMoney)
      ? window.Shopify.formatMoney(cents)
      : (cents / 100).toLocaleString(undefined, { style: 'currency', currency: window.themeCurrency || 'INR' });

  /* ------------------------------------------------------------------ overlay */
  const overlay = () => $('[data-site-overlay]');
  let lastFocused = null;

  function openPanel(panel) {
    if (!panel) return;
    lastFocused = document.activeElement;
    panel.setAttribute('data-open', 'true');
    panel.removeAttribute('hidden');
    const ov = overlay();
    if (ov) {
      ov.hidden = false;
      requestAnimationFrame(() => ov.setAttribute('data-visible', 'true'));
    }
    document.body.style.overflow = 'hidden';
    const focusable = panel.querySelector('button, a, input, select, textarea');
    if (focusable) focusable.focus();
  }

  function closePanels() {
    $$('[data-panel]').forEach((p) => p.setAttribute('data-open', 'false'));
    const ov = overlay();
    if (ov) {
      ov.setAttribute('data-visible', 'false');
      setTimeout(() => { ov.hidden = true; }, 300);
    }
    document.body.style.overflow = '';
    if (lastFocused) lastFocused.focus();
  }

  document.addEventListener('click', (e) => {
    const openTrigger = e.target.closest('[data-panel-open]');
    if (openTrigger) {
      e.preventDefault();
      openPanel($('[data-panel="' + openTrigger.getAttribute('data-panel-open') + '"]'));
      return;
    }
    if (e.target.closest('[data-panel-close]') || e.target.closest('[data-site-overlay]')) {
      closePanels();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closePanels();
  });

  /* ------------------------------------------------ mobile submenu accordions */
  $$('[data-submenu-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.nextElementSibling;
      const open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open));
      if (target) target.hidden = open;
    });
  });

  /* ------------------------------------------------------------ header search */
  const searchToggle = $('[data-search-toggle]');
  if (searchToggle) {
    searchToggle.addEventListener('click', () => {
      const panel = $('[data-search-panel]');
      if (!panel) return;
      const open = panel.getAttribute('data-open') === 'true';
      panel.setAttribute('data-open', String(!open));
      searchToggle.setAttribute('aria-expanded', String(!open));
      if (!open) {
        const input = panel.querySelector('input[type="search"]');
        if (input) input.focus();
      }
    });
  }

  /* -------------------------------------------------------------- hero slider */
  $$('[data-hero]').forEach((hero) => {
    const slides = $$('[data-hero-slide]', hero);
    if (slides.length < 2) return;
    const dots = $$('[data-hero-dot]', hero);
    const interval = parseInt(hero.getAttribute('data-autoplay') || '0', 10);
    let index = 0;
    let timer = null;

    function show(next) {
      index = (next + slides.length) % slides.length;
      slides.forEach((s, i) => s.setAttribute('aria-hidden', String(i !== index)));
      dots.forEach((d, i) => d.setAttribute('aria-selected', String(i === index)));
    }
    function start() {
      if (interval > 0) timer = setInterval(() => show(index + 1), interval);
    }
    function stop() { if (timer) clearInterval(timer); }

    dots.forEach((dot, i) => dot.addEventListener('click', () => { stop(); show(i); start(); }));
    const prev = $('[data-hero-prev]', hero);
    const next = $('[data-hero-next]', hero);
    if (prev) prev.addEventListener('click', () => { stop(); show(index - 1); start(); });
    if (next) next.addEventListener('click', () => { stop(); show(index + 1); start(); });

    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', start);

    // touch swipe
    let startX = null;
    hero.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    hero.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const delta = e.changedTouches[0].clientX - startX;
      if (Math.abs(delta) > 45) show(index + (delta < 0 ? 1 : -1));
      startX = null;
    });

    show(0);
    start();
  });

  /* -------------------------------------------------------- milestone slider */
  $$('[data-milestones]').forEach((timeline) => {
    const slides = $$('[data-milestone-slide]', timeline);
    if (!slides.length) return;
    let index = Math.min(1, slides.length - 1);

    function show(next) {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== index)));
    }

    const prev = $('[data-milestone-prev]', timeline);
    const next = $('[data-milestone-next]', timeline);
    if (prev) prev.addEventListener('click', () => show(index - 1));
    if (next) next.addEventListener('click', () => show(index + 1));
    show(index);
  });

  /* ------------------------------------------------------------ scroll reveal */
  if (document.body.classList.contains('animations-on') && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    $$('[data-animate]').forEach((el, i) => {
      el.style.transitionDelay = Math.min(i % 6, 5) * 70 + 'ms';
      io.observe(el);
    });
  } else {
    $$('[data-animate]').forEach((el) => el.classList.add('is-visible'));
  }

  /* ------------------------------------------------------------- quantity +/- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const wrap = btn.closest('.quantity');
    const input = $('input', wrap);
    if (!input) return;
    const step = btn.getAttribute('data-qty') === 'up' ? 1 : -1;
    const min = parseInt(input.getAttribute('min') || '1', 10);
    input.value = Math.max(min, (parseInt(input.value, 10) || min) + step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* --------------------------------------------------------- product gallery */
  $$('[data-gallery]').forEach((gallery) => {
    const main = $('[data-gallery-main]', gallery);
    $$('[data-gallery-thumb]', gallery).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.getAttribute('data-image');
        const alt = thumb.getAttribute('data-alt') || '';
        if (main && src) { main.src = src; main.alt = alt; }
        $$('[data-gallery-thumb]', gallery).forEach((t) => t.setAttribute('aria-current', 'false'));
        thumb.setAttribute('aria-current', 'true');
      });
    });
  });

  /* -------------------------------------------------------- variant selection */
  $$('[data-product-form]').forEach((form) => {
    const dataEl = $('[data-variants-json]', form.closest('[data-product-root]') || document);
    if (!dataEl) return;
    let variants = [];
    try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

    const idInput = $('[data-variant-id]', form);
    const submit = $('[data-add-to-cart]', form);
    const priceEl = $('[data-product-price]', form.closest('[data-product-root]'));
    const skuEl = $('[data-product-sku]', form.closest('[data-product-root]'));
    const mainImage = $('[data-gallery-main]', form.closest('[data-product-root]'));

    function selectedOptions() {
      const groups = $$('[data-option-index]', form);
      return groups.map((g) => {
        const checked = $('input:checked', g) || $('select', g);
        return checked ? checked.value : null;
      });
    }

    function update() {
      const opts = selectedOptions();
      const match = variants.find((v) => v.options.every((o, i) => o === opts[i]));
      if (!match) {
        if (submit) { submit.disabled = true; submit.textContent = submit.getAttribute('data-unavailable') || 'Unavailable'; }
        return;
      }
      if (idInput) idInput.value = match.id;
      if (submit) {
        submit.disabled = !match.available;
        submit.textContent = match.available
          ? (submit.getAttribute('data-add') || 'Add to cart')
          : (submit.getAttribute('data-sold-out') || 'Sold out');
      }
      if (priceEl && match.price_html) priceEl.innerHTML = match.price_html;
      if (skuEl) skuEl.textContent = match.sku || '';
      if (mainImage && match.featured_image) { mainImage.src = match.featured_image; }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url.toString());
    }

    form.addEventListener('change', update);
    update();
  });

  /* ---------------------------------------------------------------- AJAX cart */
  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Cart request failed');
    }
    return res.json();
  }

  function setCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  async function refreshDrawer() {
    const drawer = $('[data-cart-drawer-content]');
    if (!drawer) return;
    const res = await fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + '?section_id=cart-drawer' : '/?section_id=cart-drawer');
    const html = await res.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('[data-cart-drawer-content]');
    if (fresh) drawer.innerHTML = fresh.innerHTML;
  }

  // add to cart (product forms + quick add)
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ajax-cart]');
    if (!form) return;
    e.preventDefault();
    const button = $('[type="submit"]', form);
    const original = button ? button.textContent : '';
    if (button) { button.disabled = true; button.textContent = 'Adding…'; }
    try {
      const formData = new FormData(form);
      const payload = {
        items: [{ id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }]
      };
      const cart = await cartRequest('/cart/add.js', payload);
      if (typeof cart.item_count === 'number') setCartCount(cart.item_count);
      const cartRes = await fetch('/cart.js');
      const cartJson = await cartRes.json();
      setCartCount(cartJson.item_count);
      await refreshDrawer();
      const drawer = $('[data-panel="cart"]');
      if (drawer) openPanel(drawer);
      else window.location.href = '/cart';
    } catch (err) {
      alert(err.message);
    } finally {
      if (button) { button.disabled = false; button.textContent = original; }
    }
  });

  // change quantity / remove inside drawer or cart page
  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    try {
      const cart = await cartRequest('/cart/change.js', { id: remove.getAttribute('data-cart-remove'), quantity: 0 });
      setCartCount(cart.item_count);
      if (document.body.classList.contains('template--cart')) window.location.reload();
      else await refreshDrawer();
    } catch (err) { alert(err.message); }
  });

  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-cart-quantity]');
    if (!input) return;
    try {
      const cart = await cartRequest('/cart/change.js', {
        id: input.getAttribute('data-cart-quantity'),
        quantity: Number(input.value)
      });
      setCartCount(cart.item_count);
      if (document.body.classList.contains('template--cart')) window.location.reload();
      else await refreshDrawer();
    } catch (err) { alert(err.message); }
  });

  /* ---------------------------------------------- collection sort + auto form */
  $$('[data-auto-submit]').forEach((el) => {
    el.addEventListener('change', () => {
      const form = el.closest('form');
      if (form) form.submit();
    });
  });

  window.themeMoney = money;
})();
